import { convertDateToBrazilFormat } from '@repo/ui';
import {
  ProfessionalsWorkJourneyDay,
  ProfessionalsWorkJourneyOrganization,
  ProfessionalsWorkJourneyProfessional,
  ProfessionalsWorkJourneyTimeLog,
  ProfessionalsWorkJourneyWorkLogs,
  TimeLogStatus,
} from 'domain/models';

export const timeLogStatusLabel: Record<TimeLogStatus, string> = {
  [TimeLogStatus.ON_TIME]: 'No horário',
  [TimeLogStatus.LATE]: 'Atraso',
  [TimeLogStatus.EARLY]: 'Saída antecipada',
  [TimeLogStatus.MISSING]: 'Sem registro',
};

export const timeLogStatusVariant: Record<TimeLogStatus, string> = {
  [TimeLogStatus.ON_TIME]: 'success',
  [TimeLogStatus.LATE]: 'warning',
  [TimeLogStatus.EARLY]: 'warning',
  [TimeLogStatus.MISSING]: 'secondary',
};

const weekDayFormatter = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'long',
  timeZone: 'UTC',
});

export const getWeekDayLabel = (date: string) => {
  if (!date) {
    return '-';
  }

  const rawDate = date.length > 10 ? date.slice(0, 10) : date;
  const [year, month, day] = rawDate.split('-').map(Number);

  if (!year || !month || !day) {
    return '-';
  }

  const formattedWeekDay = weekDayFormatter.format(
    new Date(Date.UTC(year, month - 1, day)),
  );

  return formattedWeekDay.charAt(0).toUpperCase() + formattedWeekDay.slice(1);
};

export const getInitials = (name: string) => {
  if (!name) {
    return '-';
  }

  const nameParts = name.trim().split(/\s+/);
  const firstInitial = nameParts[0]?.[0] || '';
  const secondInitial = nameParts[1]?.[0] || nameParts[0]?.[1] || '';

  return `${firstInitial}${secondInitial}`.toUpperCase();
};

export const getDayTimeLogs = (
  day: ProfessionalsWorkJourneyDay,
): ProfessionalsWorkJourneyTimeLog[] =>
  [
    day.workLogs?.firstEntry,
    day.workLogs?.firstExit,
    day.workLogs?.secondEntry,
    day.workLogs?.secondExit,
  ].filter(Boolean);

/**
 * A API envia apenas dias com ao menos uma batida — dias inteiramente sem
 * registro nao vem em `days`. Por isso nao existe contador de dias sem
 * registro aqui: ele nao e derivavel da resposta.
 */
export type ProfessionalJourneySummary = {
  totalDays: number;
  daysWithRecord: number;
  lateDays: number;
  earlyExitDays: number;
};

export const getProfessionalSummary = (
  professional: ProfessionalsWorkJourneyProfessional,
): ProfessionalJourneySummary => {
  const days = professional.days || [];

  return days.reduce<ProfessionalJourneySummary>(
    (summary, day) => {
      const timeLogs = getDayTimeLogs(day);

      const hasRecord = timeLogs.some(
        (timeLog) => timeLog.status !== TimeLogStatus.MISSING,
      );
      const hasLate = timeLogs.some(
        (timeLog) => timeLog.status === TimeLogStatus.LATE,
      );
      const hasEarlyExit = timeLogs.some(
        (timeLog) => timeLog.status === TimeLogStatus.EARLY,
      );

      return {
        totalDays: summary.totalDays + 1,
        daysWithRecord: summary.daysWithRecord + (hasRecord ? 1 : 0),
        lateDays: summary.lateDays + (hasLate ? 1 : 0),
        earlyExitDays: summary.earlyExitDays + (hasEarlyExit ? 1 : 0),
      };
    },
    {
      totalDays: 0,
      daysWithRecord: 0,
      lateDays: 0,
      earlyExitDays: 0,
    },
  );
};

/**
 * Vinculo do profissional no dia. A API pode devolver um vinculo diferente por
 * dia (ex.: Preceptor(a) em janeiro e Bolsista Professor(a) em marco), por isso
 * o vinculo aparece na linha da tabela e nao no cabecalho.
 */
export const getDayRole = (
  day: ProfessionalsWorkJourneyDay,
  professional: ProfessionalsWorkJourneyProfessional,
) => day.role || professional.role || '-';

export const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const DIACRITICS_REGEX = new RegExp('[\\u0300-\\u036f]', 'g');

/** Texto sem acentos e em minusculas, para comparacao na busca local. */
export const normalizeText = (value: string) =>
  (value || '')
    .normalize('NFD')
    .replace(DIACRITICS_REGEX, '')
    .toLowerCase()
    .trim();

/**
 * Linha da tabela exibida dentro de cada organizacao: um dia de um profissional.
 * Os campos de texto ficam no nivel raiz porque a busca local do DataTable so
 * enxerga chaves string do proprio objeto (ver `keysSearch`).
 */
export type WorkJourneyTableRow = {
  id: string;
  date: string;
  formattedDate: string;
  weekDay: string;
  role: string;
  professionalId: string;
  professionalName: string;
  professionalPhoto: string;
  locationName: string;
  locationAddress: string;
  workLogs: ProfessionalsWorkJourneyWorkLogs;
};

/** Achata os dias de todos os profissionais da organizacao em linhas de tabela. */
export const buildOrganizationRows = (
  organization: ProfessionalsWorkJourneyOrganization,
): WorkJourneyTableRow[] =>
  (organization.professionals || []).flatMap(
    (professional, professionalIndex) =>
      (professional.days || []).map((day, dayIndex) => ({
        id: `${professional.id || professionalIndex}-${day.date}-${dayIndex}`,
        date: day.date,
        formattedDate: convertDateToBrazilFormat(day.date),
        weekDay: getWeekDayLabel(day.date),
        role: getDayRole(day, professional),
        professionalId: professional.id,
        professionalName: professional.name || 'Não informado',
        professionalPhoto: professional.photo,
        locationName: day.location?.name || '--',
        locationAddress: day.location?.address || '--',
        workLogs: day.workLogs,
      })),
  );
